import { useCallback } from 'react';

import { useCoreSelector } from '../../CoreStore';

import {
  ChannelVolumeCallbackType,
  ChannelVolumeValueCallbackType,
  StreamQuality,
  UiChannelType,
  VolumeChannelType,
} from './types';

export const useSettingsSlice = {
  getIsInFullscreen: (): boolean => {
    return useCoreSelector(({ settings }) => settings.isInFullscreen);
  },

  /* Language */
  currentLanguage: (): string => {
    return useCoreSelector(({ settings }) => settings.language.current);
  },
  getIsCurrentLanguageRTL: (): boolean => {
    return useCoreSelector(({ settings }) => settings.language.isRTL);
  },

  /* Stream */
  getIsInClassicView: (): boolean => {
    return useCoreSelector(({ settings }) => settings.stream.isInClassicView);
  },
  getIsStreamEnabled: (): boolean => {
    return useCoreSelector(({ settings }) => settings.stream.isEnabled && settings.stream.isEnabledBySystem);
  },
  getStreamQualityLimit: (): number => {
    return useCoreSelector(({ settings }) => settings.stream.qualityLimit);
  },
  getStreamQuality: (): StreamQuality => {
    return useCoreSelector(({ settings }) => settings.stream.quality);
  },
  /**
   * Raw stream latency value from video player.
   * @returns Updated stream latency in milliseconds
   */
  getStreamLatency: (): number => {
    return useCoreSelector(({ settings }) => settings.stream.streamLatency);
  },

  getAnimationToggle: (): boolean => {
    return useCoreSelector(({ settings }) => settings.isAnimationToggle);
  },
  getIsSecondBetBoardToggle: (): boolean => {
    return useCoreSelector(({ settings }) => settings.isSecondBetBoardToggle);
  },

  // Chat settings
  getShowOtherMessages: (): boolean => {
    return useCoreSelector(({ settings }) => settings.showOtherMessages);
  },

  /* Volume */
  getNoVolumeControl: (): boolean | undefined => {
    return useCoreSelector(({ settings }) => settings.volume.noVolumeControl);
  },
  getUIVolumeChannel: (): UiChannelType => {
    return useCoreSelector(({ settings }) => settings.volume.uiChannel);
  },
  getVolumeChannels: (): Record<string, VolumeChannelType> => {
    return useCoreSelector(({ settings }) => settings.volume.channels);
  },
  getVolumeChannelsReady: (channels: string[]): boolean => {
    return useCoreSelector(({ settings }) => {
      return channels.every((channel) => {
        return settings.volume.channels[channel] !== undefined;
      });
    });
  },
  getChannelVolumes: (): Record<string, number> => {
    const masterVolume = masterVolumeValue();
    const channels = useCoreSelector(({ settings }) => settings.volume.channels);
    const volumes: Record<string, number> = {};
    for (const channelName in channels) {
      const channelVolume = channels[channelName]?.value || 0;
      volumes[channelName] = masterVolume * channelVolume;
    }
    return volumes;
  },
  getAllIsMutedCheck: (): boolean => {
    let isMuted = true;
    const channels = useCoreSelector(({ settings }) => settings.volume.channels);
    Object.values(channels).forEach((chanel) => {
      if (chanel.value > 0) isMuted = false;
    });
    return isMuted;
  },
  getVolumeChannel: (): ChannelVolumeCallbackType => {
    return useCallback((channelName: string) => channelVolume(channelName), []);
  },
  getVolumeChannelRawValue: (): ChannelVolumeValueCallbackType => {
    return useCallback((channelName: string) => channelVolumeValue(channelName), []);
  },
  getVolumeChannelValue: (channelName: string): number => {
    const masterVolume = masterVolumeValue();
    const channelVolume = channelVolumeValue(channelName);
    return masterVolume * channelVolume;
  },
  getMasterChannel: (): VolumeChannelType => {
    return useCoreSelector(({ settings }) => settings.volume.master);
  },
  getWasVolumeRestored: (): boolean => {
    return useCoreSelector(({ settings }) => settings.volume.restored || false);
  },
  getWasAnySoundChannelEnabled: (): boolean => {
    return useCoreSelector(({ settings }) => {
      const channels = settings.volume.channels;

      return Object.values(channels).some((channel) => channel.previousValue);
    });
  },
  getMasterChannelValue: (): number => masterVolumeValue(),
};

const masterVolumeValue = (): number => {
  return useCoreSelector(({ settings }) => {
    if (!settings.isGameVisible) return 0;
    const masterVolume = settings.volume.master;
    return masterVolume.temporaryValue ?? masterVolume.value;
  });
};

const channelVolume = (channelName: string): VolumeChannelType => {
  return useCoreSelector(({ settings }) => {
    const channel = settings.volume.channels[channelName];
    if (channel) {
      return channel;
    } else {
      throw new Error(`Volume channel ${channelName} does not exist`);
    }
  });
};

const channelVolumeValue = (channelName: string): number => {
  const channel = channelVolume(channelName);
  if (channel) {
    return channel.temporaryValue ?? channel.value;
  } else {
    throw new Error(`Volume channel ${channelName} does not exist`);
  }
};
